import { Box, Button, Container, Grid, Stack, Typography } from "@mui/material"
import { Link } from "react-router-dom"

import Background from "../assets/ContactHeader.jpg"
import ContactForm from "./ContactForm"
import ContactLinksList from "./ContactLinksList"
import { homeCopy } from "../content/homeCopy"
import { PHONE_DISPLAY, PHONE_HREF } from "../constants/contactInfo"
import { overlineSx, pillButtonSx } from "../styles/sectionStyles"

const sectionStyles = {
    dark: {
        position: "relative",
        py: { xs: 7, md: 10 },
        color: "#fff",
        overflow: "hidden",
        backgroundImage: `
            linear-gradient(
                105deg,
                rgba(0, 4, 53, 0.96) 0%,
                rgba(0, 4, 53, 0.88) 42%,
                rgba(0, 4, 53, 0.55) 100%
            ),
            url('${Background}')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center right",
        backgroundRepeat: "no-repeat",
    },
    light: {
        position: "relative",
        py: { xs: 6, md: 9 },
        background: `
            radial-gradient(ellipse 70% 50% at 0% 0%, rgba(0, 163, 255, 0.07), transparent 55%),
            radial-gradient(ellipse 50% 40% at 100% 100%, rgba(0, 4, 53, 0.04), transparent 50%),
            linear-gradient(180deg, #f3f7fb 0%, #ffffff 55%, #f7f9fc 100%)
        `,
    },
}

const copy = {
    dark: homeCopy.contact.dark,
    light: homeCopy.contact.light,
}

const ContactSection = ({
    variant = "light",
    id,
    overline = "Get in touch",
    title = "Ready to get on the road?",
}) => {
    const styles = sectionStyles[variant]
    const content = {
        ...copy[variant],
        showCallButton: variant === "dark",
    }
    const isDark = variant === "dark"

    return (
        <Box component="section" id={id} sx={styles}>
            {isDark && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(ellipse 50% 60% at 85% 50%, rgba(0, 163, 255, 0.12), transparent 60%)",
                        pointerEvents: "none",
                    }}
                />
            )}

            <Container maxWidth="lg" sx={{ position: "relative" }}>
                <Grid container spacing={{ xs: 4, lg: 6 }} alignItems="stretch">
                    <Grid item xs={12} lg={5}>
                        <Stack
                            spacing={3}
                            height="100%"
                            justifyContent="center"
                            data-aos="fade-right"
                            data-aos-duration="800"
                        >
                            <Box>
                                <Typography variant="overline" sx={overlineSx}>
                                    {overline}
                                </Typography>
                                <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    sx={{
                                        mt: 1,
                                        mb: 1.5,
                                        lineHeight: 1.15,
                                        color: isDark ? "inherit" : "#000435",
                                    }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        ...(isDark
                                            ? { opacity: 0.82, lineHeight: 1.7, maxWidth: 420 }
                                            : {
                                                  color: "text.secondary",
                                                  lineHeight: 1.7,
                                                  maxWidth: 420,
                                              }),
                                    }}
                                >
                                    {content.description}
                                </Typography>
                            </Box>

                            <ContactLinksList variant={variant} />

                            {content.showCallButton && (
                                <Box>
                                    <Button
                                        component={Link}
                                        to={PHONE_HREF}
                                        variant="outlined"
                                        color="secondary"
                                        size="large"
                                        sx={{
                                            ...pillButtonSx,
                                            px: 3,
                                            borderColor: "rgba(0, 163, 255, 0.55)",
                                            color: "#fff",
                                            "&:hover": {
                                                borderColor: "#00A3FF",
                                                background: "rgba(0, 163, 255, 0.1)",
                                            },
                                        }}
                                    >
                                        Call {PHONE_DISPLAY}
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    </Grid>

                    <Grid item xs={12} lg={7}>
                        <Box
                            data-aos="fade-left"
                            data-aos-duration="800"
                            sx={{
                                height: "100%",
                                p: { xs: 2.5, sm: 3.5 },
                                borderRadius: 3,
                                background: "#fff",
                                color: "#000435",
                                boxShadow: isDark
                                    ? "0 28px 60px rgba(0, 0, 0, 0.35)"
                                    : "0 24px 50px rgba(0, 4, 53, 0.1)",
                                border: isDark
                                    ? "1px solid rgba(255,255,255,0.2)"
                                    : "1px solid rgba(0, 4, 53, 0.06)",
                            }}
                        >
                            <Stack spacing={0.75} mb={3}>
                                <Typography variant="h5" fontWeight={800} color="#000435">
                                    Send an enquiry
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {homeCopy.contact.formSubheading}
                                </Typography>
                            </Stack>
                            <ContactForm submitLabel="Send enquiry" variant="panel" />
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default ContactSection
